<script setup lang="ts">
import { computed, watch } from 'vue'
import { useHead } from '@unhead/vue'
import { useRoute } from 'vue-router'
import CenteredPageContainer from '@/components/molecules/CenteredPageContainer.vue'
import BreadcrumbList from '@/components/molecules/BreadcrumbList.vue'
import type { BreadCrumbLink } from '@/types/app'
import OkaneKollegeUserPageSidebar from './OkaneKollegeUserPageSidebar/OkaneKollegeUserPageSidebar.vue'
import { useLoadKollegeUserPageQuery } from './OkaneKollegeUserPage.generated'

const route = useRoute('OkaneKollegeUser')
console.log('🚗', route.name)

useHead({
  title: 'オカネカレッジ',
})

const { userId } = route.params

const { result, error } = useLoadKollegeUserPageQuery({
  id: userId,
})

watch([result, error], ([result, error]) => {
  if (error) {
    throw new Error(error.message)
  }
  if (result == null) {
    throw new Error('result is null')
  }
})

const user = computed(() => {
  return result.value?.kollegeUser
})

const userName = computed(() => {
  return user.value?.profile
    ? `${user.value.profile?.familyName ?? ''} ${user.value.profile?.givenName ?? ''}`
    : '名前なしユーザー'
})

const allInstructors = computed(() => {
  return result.value?.kollegeInstructors ?? []
})

const breadcrumbs = computed<ReadonlyArray<BreadCrumbLink>>(() => {
  const breadcrumbs = route.meta.breadcrumbs ?? []
  return [
    ...breadcrumbs,
    {
      href: '',
      text: `${userName.value}のアカウント`,
    },
  ]
})
</script>

<template>
  <CenteredPageContainer v-if="user" :class="$style.page">
    <BreadcrumbList :links="breadcrumbs" />
    <main :class="[$style.page__main, $style.main]">
      <OkaneKollegeUserPageSidebar :user="user" />
      <section :class="$style.main__content">
        <router-view
          :user-id="userId"
          :user="user"
          :all-instructors="allInstructors"
        />
      </section>
    </main>
  </CenteredPageContainer>
</template>

<style lang="scss" module>
.page {
  &__main {
    display: flex;
    align-items: flex-start;
    gap: 40px;
    margin-top: 16px;
  }
}

.main {
  &__content {
    flex: 1 0 0;
    background-color: $color-surface-primary;
    border-radius: $border-radius-v2-rounder;
    box-shadow: $shadow-4;
    padding: 24px;
  }
}
</style>
